import moment from 'moment'

import Box from 'app/components/design-system-components/Box'
import Card from 'app/components/design-system-components/Card'
import {
  Heading,
  Text,
} from 'app/components/design-system-components/typography'
import type { AuditTombstoneStatus } from 'app/sm/proof_of_income/financialInsightsStatus.types'

interface Props {
  audit: AuditTombstoneStatus
}

// DRAFT COPY — pending CS review before merge (design Open Question #1).
// These strings satisfy the CDR human-readable obligation but have not been
// reviewed by the Customer Success team for brand voice. Do not treat as final.
//
// Each tombstone status has two variants:
// - `withData`: data still retained (cleanup job hasn't run yet, dataDeletedAt is null)
// - `dataRemoved`: data has been purged (dataDeletedAt is set)
const STATUS_COPY: Record<
  AuditTombstoneStatus['status'],
  { withData: string; dataRemoved: string }
> = {
  expired: {
    withData:
      'Your Financial Insights consent has expired. Your financial data is scheduled for removal from our systems.',
    dataRemoved:
      'Your Financial Insights consent has expired. Your consent period ended and your financial data has been removed from our systems.',
  },
  revoked: {
    withData:
      'You revoked your Financial Insights consent. Your financial data is scheduled for removal from our systems.',
    dataRemoved:
      'You revoked your Financial Insights consent. Because you ended your consent, your financial data has been removed from our systems.',
  },
  data_deleted: {
    withData:
      'Your Financial Insights data has been removed. Your consent period ended and all associated financial data has been deleted from our systems.',
    dataRemoved:
      'Your Financial Insights data has been removed. Your consent period ended and all associated financial data has been deleted from our systems.',
  },
}

const fmt = (iso?: string | null): string =>
  iso ? moment(iso).format('D MMM YYYY') : '—'

const getMessage = (audit: AuditTombstoneStatus): string => {
  const copy = STATUS_COPY[audit.status]
  return audit.dataDeletedAt ? copy.dataRemoved : copy.withData
}

// The "Consent expired" row only makes sense when the consent has actually
// expired (either naturally, or after data was deleted at expiry). For a
// revoked tombstone, the consent ended on revokedAt — not expiresAt, which
// may still be in the future. Render a status-appropriate "consent ended" row.
const renderConsentEndedRow = (audit: AuditTombstoneStatus) => {
  if (audit.status === 'revoked') {
    return (
      <>
        <Text as="dt" m={0} fontWeight="bold" color="dark-gray">
          Consent revoked
        </Text>
        <Text as="dd" m={0} color="mid-gray">
          {fmt(audit.revokedAt)}
        </Text>
      </>
    )
  }

  return (
    <>
      <Text as="dt" m={0} fontWeight="bold" color="dark-gray">
        Consent expired
      </Text>
      <Text as="dd" m={0} color="mid-gray">
        {fmt(audit.expiresAt)}
      </Text>
    </>
  )
}

export const FinancialInsightsAuditCard = ({ audit }: Props) => (
  <Card
    headerTitle={
      <Heading level={4} displayingLevel={4} m={0}>
        Financial Insights — audit record
      </Heading>
    }
    headerImage={null}
    internalComponent={
      <Box data-testid="fi-audit-card">
        <Text color="mid-gray" mb={3} lineHeight="copy">
          {getMessage(audit)}
        </Text>
        <Box
          as="dl"
          m={0}
          display="grid"
          gridTemplateColumns="auto 1fr"
          gridColumnGap={4}
          gridRowGap={1}
        >
          <Text as="dt" m={0} fontWeight="bold" color="dark-gray">
            Consent granted
          </Text>
          <Text as="dd" m={0} color="mid-gray">
            {fmt(audit.consentedAt)}
          </Text>
          {renderConsentEndedRow(audit)}
          <Text as="dt" m={0} fontWeight="bold" color="dark-gray">
            Data removed
          </Text>
          <Text as="dd" m={0} color="mid-gray">
            {fmt(audit.dataDeletedAt)}
          </Text>
        </Box>
      </Box>
    }
  />
)
